import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type NexoraUser } from '../context/AuthContext';

/* ── Loading spinner ─────────────────────────────────────────────────────── */

const Spinner: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--nx-navy)',
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        border: '3px solid rgba(26,108,246,0.2)',
        borderTopColor: '#1A6CF6',
        borderRadius: '50%',
        animation: 'nx-spin 0.7s linear infinite',
      }}
    />
  </div>
);

/* ── 403 Forbidden ───────────────────────────────────────────────────────── */

const ForbiddenPage: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--nx-navy)',
      gap: 16,
      fontFamily: 'var(--font-body)',
    }}
  >
    <div
      style={{
        fontSize: 72,
        fontFamily: 'Syne, sans-serif',
        fontWeight: 800,
        background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        lineHeight: 1,
      }}
    >
      403
    </div>
    <p style={{ fontSize: 16, color: 'var(--nx-text-secondary)', margin: 0 }}>
      You don't have permission to view this page.
    </p>
    <a
      href="/dashboard"
      style={{
        marginTop: 8,
        padding: '10px 24px',
        background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
        color: '#fff',
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 600,
        textDecoration: 'none',
      }}
    >
      Back to Dashboard
    </a>
  </div>
);

/* ── ProtectedRoute ──────────────────────────────────────────────────────── */

interface Props {
  children: React.ReactNode;
  allowedRoles?: Array<NexoraUser['role']>;
}

const ProtectedRoute: React.FC<Props> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) return <Spinner />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    if (user.role === 'SUPER_ADMIN') {
      return <Navigate to="/admin" replace />;
    }
    return <ForbiddenPage />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
